import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clientsAPI } from '../../api/client';
import { providersAPI } from '../../api/traceability';
import { useAuth } from '../../context/AuthContext';
import { errorMessage } from '../../lib/evaluationIntegrity';
import { personName, isLeader, rows, SERVICE_TYPES, fetchAllRows } from '../../lib/traceability';
import ProviderAssignments, { AssignmentDetails } from './ProviderAssignments';
import { Field, FormFields } from './Fields';

const providerFields = [
  ['trade_name', 'Nombre comercial', { required: true }], ['legal_name', 'Razón social'],
  ['service_type', 'Tipo de servicio', { options: SERVICE_TYPES, required: true }],
  ['contact_name', 'Responsable', { required: true }],
  ['contact_phone', 'Teléfono', { required: true, type: 'tel' }],
  ['contact_email', 'Correo', { required: true, type: 'email' }],
  ['notes', 'Notas', { multiline: true }],
];
const assignmentFields = [
  ['service_date', 'Fecha del servicio', { type: 'date', required: true }],
  ['service_time_start', 'Hora de inicio', { type: 'time' }],
  ['service_time_end', 'Hora de fin', { type: 'time' }],
  ['requester_name', 'Solicitante / organizador', { required: true }],
  ['organizer_company', 'Empresa organizadora'], ['units', 'Unidades', { type: 'number', min: 0, step: 1 }],
  ['observations', 'Observaciones', { multiline: true }],
  ['status', 'Estatus', { options: {
    confirmado: 'Confirmado', presente: 'Presente', no_se_presento: 'No se presentó', cancelado: 'Cancelado',
  } }],
];

export function ProviderForm({ initial = {}, onSaved, onCancel }) {
  const [form, setForm] = useState({ service_type: 'ambulancia', ...initial });
  const [clients, setClients] = useState([]);
  const [clientError, setClientError] = useState('');
  const loadClients = useCallback(async () => {
    try {
      setClients(await fetchAllRows(clientsAPI.list, {}, 500));
      setClientError('');
    } catch {
      setClientError('No se pudieron cargar los clientes');
    }
  }, []);
  useEffect(() => { loadClients(); }, [loadClients]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const data = Object.fromEntries(providerFields.map(([key]) => [key, form[key] || '']));
      data.client_id = form.client_id || null;
      data.service_type_other = form.service_type === 'otro' ? form.service_type_other : '';
      const response = initial.id
        ? await providersAPI.update(initial.id, data) : await providersAPI.create(data);
      onSaved(response.data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="soem-card p-4 space-y-3">
      <h3 className="font-bold">{initial.id ? 'Editar proveedor' : 'Nuevo proveedor'}</h3>
      <FormFields fields={providerFields} value={form} onChange={setForm} />
      <Field label="Cliente exclusivo (opcional)" value={form.client_id}
        options={{ '': 'Compartido',
          ...(form.client_id ? {
            [form.client_id]: clients.find((c) => c.id === form.client_id)?.name || 'Cliente actual',
          }
            : {}), ...Object.fromEntries(clients.map((client) => [client.id, client.name])),
        }} onChange={(client_id) => setForm({ ...form, client_id })} />
      {clientError && <p role="alert">{clientError}
        <button type="button" onClick={loadClients}>Reintentar clientes</button></p>}
      {form.service_type === 'otro' && <Field label="Otro servicio" required
        value={form.service_type_other} onChange={(value) => setForm({ ...form, service_type_other: value })} />}
      {form.contact_phone && !/^(\d{10}|\+\d[\d\s()-]+)$/.test(form.contact_phone) && (
        <p>Revisa el teléfono: se sugieren 10 dígitos MX o prefijo internacional +.</p>
      )}
      {error && <p role="alert">{error}</p>}
      <button className="soem-btn-primary p-2" disabled={busy}>Guardar proveedor</button>
      <button type="button" className="soem-btn-secondary p-2" onClick={onCancel}>Cancelar</button>
    </form>
  );
}

export default function Providers({ eventId, eventName, canEdit = true, onChanged }) {
  const { activeRole } = useAuth();
  const [items, setItems] = useState([]);
  const requestRef = useRef(0);
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState('');
  const [service, setService] = useState('');
  const [editor, setEditor] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const editable = canEdit && ['super_admin', 'lider_proyecto', 'levantador'].includes(activeRole);
  const load = useCallback(async () => {
    // #Fase3-A4 — Una búsqueda anterior no debe reemplazar el filtro que sigue en pantalla.
    const request = ++requestRef.current;
    setError('');
    try {
      const result = eventId ? rows((await providersAPI.forEvent(eventId)).data)
        : await fetchAllRows(providersAPI.list, { q: query, service_type: service || undefined });
      const catalogResult = eventId && editable
        ? await fetchAllRows(providersAPI.list, { q: query, service_type: service || undefined }) : null;
      if (request !== requestRef.current) return;
      setItems(result);
      if (catalogResult) setCatalog(catalogResult);
    } catch (err) {
      if (request === requestRef.current) setError(errorMessage(err, 'No se pudieron cargar los proveedores'));
    }
  }, [eventId, query, service, editable]);
  useEffect(() => { load(); }, [load]);
  const mutate = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
      setAssignment(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-bold">Proveedores</h2>
      {error && <p role="alert">{error} <button onClick={load}>Reintentar</button></p>}
      {(!eventId || assignment) && <div className="grid md:grid-cols-2 gap-3">
        <Field label="Buscar nombre, razón social o responsable" value={query} onChange={setQuery} />
        <Field label="Tipo de servicio" options={{ '': 'Todos', ...SERVICE_TYPES }}
          value={service} onChange={setService} />
      </div>}
      {editable && <button className="soem-btn-primary p-2" onClick={() => setEditor({})}>
        + Nuevo proveedor
      </button>}
      {eventId && editable && <button className="soem-btn-secondary p-2"
        onClick={() => setAssignment({ status: 'confirmado' })}>Asignar proveedor</button>}
      {editable && editor && <ProviderForm initial={editor} onCancel={() => setEditor(null)} onSaved={(provider) => {
        setEditor(null);
        load();
        if (eventId) setAssignment({ provider_id: provider.id, status: 'confirmado' });
      }} />}
      {editable && assignment && <form className="soem-card p-4 space-y-3" onSubmit={(event) => {
        event.preventDefault();
        const data = Object.fromEntries(assignmentFields.map(([key]) => [key, assignment[key] || null]));
        data.provider_id = assignment.provider_id;
        data.units = assignment.units === '' || assignment.units == null ? null : Number(assignment.units);
        mutate(() => assignment.id ? providersAPI.updateAssignment(assignment.id, data)
          : providersAPI.assign(eventId, data));
      }}>
        <Field label="Proveedor" required value={assignment.provider_id}
          options={{ '': 'Selecciona',
            ...(assignment.provider_id ? { [assignment.provider_id]: 'Proveedor seleccionado' } : {}),
            ...Object.fromEntries(catalog.filter((p) => !p.is_archived)
            .map((p) => [p.id, p.trade_name])) }}
          onChange={(provider_id) => setAssignment({ ...assignment, provider_id })} />
        <FormFields fields={assignmentFields} value={assignment} onChange={setAssignment} />
        <button disabled={busy} className="soem-btn-primary p-2">Guardar asignación</button>
        <button type="button" onClick={() => setAssignment(null)}>Cancelar</button>
      </form>}
      {!items.length && <p>Sin proveedores registrados.</p>}
      {items.map((item) => {
        const source = item.provider || item;
        // ADM-R2: reutiliza el catálogo ya cargado si una API anterior no enriquece el anidado.
        const provider = activeRole === 'cliente' ? source : {
          ...catalog.find(entry => entry.id === (source.id || item.provider_id)), ...source,
        };
        return <article key={item.id} className="soem-card p-4 space-y-2">
          <h3 className="font-bold">{provider.trade_name || 'Proveedor sin nombre'}</h3>
          <p>Tipo de servicio: {SERVICE_TYPES[provider.service_type] || 'Sin registrar'} {activeRole !== 'cliente' && provider.service_type_other}</p>
          <p>Responsable: {provider.contact_name || 'Sin registrar'}</p>
          {activeRole !== 'cliente' && <p>Razón social: {provider.legal_name || 'Sin registrar'} · {provider.notes}</p>}
          {activeRole !== 'cliente' && <p>Teléfono: {provider.contact_phone || 'Sin registrar'} · Correo: {provider.contact_email || 'Sin registrar'}</p>}
          {eventId ? <AssignmentDetails assignment={item} eventName={eventName} clientView={activeRole === 'cliente'} />
            : <ProviderAssignments provider={provider} clientView={activeRole === 'cliente'} />}
          {activeRole !== 'cliente' && <p className="text-xs">
            Cliente exclusivo: {provider.client_name || (provider.client_id ? 'Cliente asignado' : 'No')} · Creado por: {personName(provider, 'created_by')}
            {' · '}{provider.created_at} · Actualizado: {provider.updated_at}
          </p>}
          {activeRole !== 'cliente' && provider.is_archived && <p>Archivado</p>}
          {editable && <button className="soem-btn-secondary p-2"
            onClick={() => eventId ? setAssignment(item) : setEditor(item)}>Editar</button>}
          {!eventId && isLeader(activeRole) && !item.is_archived && <button disabled={busy}
            onClick={() => mutate(() => providersAPI.archive(item.id))}>Archivar</button>}
          {eventId && editable && <button disabled={busy}
            onClick={() => mutate(() => providersAPI.removeAssignment(item.id))}>Quitar asignación</button>}
        </article>;
      })}
    </section>
  );
}
